import { useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Flex,
  Box,
  Input,
  Button,
  Heading,
  Text,
  Progress,
  Spinner,
  VStack,
  HStack,
  Icon,
  Textarea,
  Badge,
  Divider,
  useColorModeValue,
} from "@chakra-ui/react";
import { FiUpload, FiCheck, FiAlertCircle, FiFileText, FiImage } from "react-icons/fi";
import { uploadDiagram, uploadDiagramText } from "../services";
import { keyframes } from "@emotion/react";

const bounce = keyframes`
  0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
  40% { transform: translateY(-10px); }
  60% { transform: translateY(-5px); }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const ACCEPTED_FILES =
  "image/*,.pdf,.xml,.json,.md,.txt,.svg";

const FILE_HINT =
  "Formatos soportados: JPG, PNG, SVG, PDF, XML, JSON, TXT, MD";

const UploadDiagram = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // 'file' | 'text'
  const [mode, setMode] = useState("file");

  // file-mode state
  const [file, setFile] = useState(null);

  // text-mode state
  const [textContent, setTextContent] = useState("");

  // shared upload state
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isAwaitingResponse, setIsAwaitingResponse] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null); // 'success' | 'saved' | 'warning' | 'error' | null
  const [errorMessage, setErrorMessage] = useState("");
  const navigateTimerRef = useRef(null);

  const fileInputRef = useRef(null);

  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const tabActiveBg = useColorModeValue("teal.50", "teal.900");
  const tabInactiveBg = useColorModeValue("gray.50", "gray.700");

  const simulateProgress = () => {
    setUploadProgress(0);
    setIsAwaitingResponse(false);
    const interval = setInterval(() => {
      setUploadProgress((prev) => {
        if (prev >= 80) {
          clearInterval(interval);
          setIsAwaitingResponse(true);
          return 80;
        }
        return Math.min(prev + Math.random() * 15, 80);
      });
    }, 200);
    return interval;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const readyFile = mode === "file" && file;
    const readyText = mode === "text" && textContent.trim().length > 0;
    if (!readyFile && !readyText) return;

    setIsUploading(true);
    setIsAwaitingResponse(false);
    setUploadStatus(null);
    setErrorMessage("");

    const progressInterval = simulateProgress();

    try {
      let response;
      if (mode === "file") {
        response = await uploadDiagram(id, file);
      } else {
        response = await uploadDiagramText(id, textContent.trim());
      }

      clearInterval(progressInterval);
      setUploadProgress(100);
      setIsAwaitingResponse(false);

      // Notify header/user-menu to refresh org credits after an analysis attempt.
      window.dispatchEvent(new Event("org-credits-refresh"));

      if (response.data.success) {
        setUploadStatus("success");
        navigateTimerRef.current = setTimeout(() => navigate(`/analysis/${id}`), 2000);
      } else if (response.data.information_system) {
        // File was saved but AI couldn't detect threats (e.g. text-only model, image not supported).
        // Navigate to analysis so the user can see the uploaded file and add threats manually.
        setUploadStatus("saved");
        setErrorMessage(response.data.message || "");
        navigateTimerRef.current = setTimeout(() => navigate(`/analysis/${id}`), 3000);
      } else {
        setUploadStatus("warning");
        setErrorMessage(
          response.data.message || "No se encontraron amenazas en el contenido analizado"
        );
      }
    } catch (error) {
      clearInterval(progressInterval);
      setUploadStatus("error");
      setIsUploading(false);
      setIsAwaitingResponse(false);

      if (error.response?.status === 402) {
        const detail = error.response?.data?.detail;
        const available = detail?.available ?? 0;
        setErrorMessage(
          `Sin créditos suficientes. Tienes ${available} crédito${available !== 1 ? 's' : ''} disponible${available !== 1 ? 's' : ''} y se requiere 1 para analizar. Recarga tu balance desde el panel de administración.`
        );
      } else {
        setErrorMessage(
          error.response?.data?.message ||
          (typeof error.response?.data?.detail === 'string' ? error.response.data.detail : null) ||
          "Error al conectar con el servidor"
        );
      }
    }
  };

  const resetUpload = () => {
    if (navigateTimerRef.current) {
      clearTimeout(navigateTimerRef.current);
      navigateTimerRef.current = null;
    }
    setFile(null);
    setTextContent("");
    setIsUploading(false);
    setUploadProgress(0);
    setIsAwaitingResponse(false);
    setUploadStatus(null);
    setErrorMessage("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const isReady =
    !isUploading &&
    uploadStatus !== "success" &&
    uploadStatus !== "saved" &&
    (mode === "file" ? !!file : textContent.trim().length > 0);

  const progressLabel =
    uploadProgress < 30
      ? mode === "file"
        ? "Procesando archivo..."
        : "Procesando texto..."
      : uploadProgress < 60
      ? "Analizando contenido..."
      : uploadProgress < 80
      ? "Generando amenazas..."
      : "Finalizando...";

  const progressStage =
    uploadProgress < 30 ? 0 : uploadProgress < 60 ? 1 : uploadProgress < 80 ? 2 : 3;

  const statusTone =
    uploadStatus === "warning"
      ? "orange"
      : uploadStatus === "error"
      ? "red"
      : uploadStatus === "success"
      ? "green"
      : "teal";

  return (
    <Flex align="center" justify="center" minHeight="100vh" bg="gray.50" p={4}>
      <Box
        p={8}
        borderWidth={1}
        borderRadius={16}
        boxShadow="2xl"
        width="520px"
        bg={bgColor}
        borderColor={borderColor}
        animation={`${fadeIn} 0.5s ease-out`}
      >
        <VStack spacing={6}>
          <Heading size="lg" textAlign="center" color="teal.600">
            <Icon as={FiUpload} mr={2} />
            Subir Diagrama de Amenazas
          </Heading>

          {/* ── TABS ── */}
          {!isUploading && uploadStatus !== "success" && (
            <HStack width="100%" spacing={0} borderRadius={10} overflow="hidden" border="1px solid" borderColor="gray.200">
              <Button
                flex={1}
                borderRadius={0}
                leftIcon={<FiImage />}
                bg={mode === "file" ? tabActiveBg : tabInactiveBg}
                color={mode === "file" ? "teal.700" : "gray.500"}
                fontWeight={mode === "file" ? "bold" : "normal"}
                borderRight="1px solid"
                borderColor="gray.200"
                _hover={{ bg: tabActiveBg, color: "teal.700" }}
                onClick={() => { setMode("file"); resetUpload(); }}
                size="sm"
                py={5}
              >
                Archivo / Imagen
              </Button>
              <Button
                flex={1}
                borderRadius={0}
                leftIcon={<FiFileText />}
                bg={mode === "text" ? tabActiveBg : tabInactiveBg}
                color={mode === "text" ? "teal.700" : "gray.500"}
                fontWeight={mode === "text" ? "bold" : "normal"}
                _hover={{ bg: tabActiveBg, color: "teal.700" }}
                onClick={() => { setMode("text"); resetUpload(); }}
                size="sm"
                py={5}
              >
                Descripción de Texto
              </Button>
            </HStack>
          )}

          {/* ── FILE MODE ── */}
          {mode === "file" && !isUploading && uploadStatus !== "success" && (
            <Box
              p={6}
              border="2px dashed"
              borderColor={file ? "teal.300" : "gray.300"}
              borderRadius={12}
              width="100%"
              textAlign="center"
              transition="all 0.3s"
              _hover={{ borderColor: "teal.400", transform: "scale(1.02)" }}
              bg={file ? "teal.50" : "gray.50"}
            >
              <Input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_FILES}
                onChange={(e) => setFile(e.target.files[0])}
                display="none"
                id="file-upload"
              />
              <label htmlFor="file-upload" style={{ cursor: "pointer", width: "100%", display: "block" }}>
                <Icon
                  as={FiUpload}
                  fontSize="3em"
                  color={file ? "teal.500" : "gray.400"}
                  mb={3}
                  animation={file ? `${bounce} 1s infinite` : undefined}
                />
                <Text fontSize="lg" color={file ? "teal.600" : "gray.500"} mb={2}>
                  {file ? file.name : "Haz clic aquí o arrastra tu archivo"}
                </Text>
                <Text fontSize="sm" color="gray.400">
                  {FILE_HINT}
                </Text>
              </label>
            </Box>
          )}

          {/* ── TEXT MODE ── */}
          {mode === "text" && !isUploading && uploadStatus !== "success" && (
            <Box width="100%">
              <Text fontSize="sm" color="gray.500" mb={2}>
                Describe la arquitectura del sistema, flujos de datos, componentes y conexiones.
                Cuanto más detallado, mejor será el análisis de amenazas.
              </Text>
              <Textarea
                placeholder={
                  "Ejemplo:\n" +
                  "El sistema tiene un cliente web que se conecta a una API REST. " +
                  "La API autentica usuarios con JWT y accede a una base de datos PostgreSQL. " +
                  "Los datos se transfieren mediante HTTPS. Existe un servicio de notificaciones por correo..."
                }
                value={textContent}
                onChange={(e) => setTextContent(e.target.value)}
                minH="200px"
                borderColor={textContent.trim() ? "teal.300" : "gray.300"}
                focusBorderColor="teal.400"
                resize="vertical"
                fontSize="sm"
                bg={textContent.trim() ? "teal.50" : "gray.50"}
              />
              <Text fontSize="xs" color="gray.400" mt={1} textAlign="right">
                {textContent.length} caracteres
              </Text>
            </Box>
          )}

          {/* ── UPLOADING STATE ── */}
          {isUploading && (
            <VStack spacing={5} width="100%" animation={`${fadeIn} 0.3s ease-out`}>
              <Box width="100%" p={4} borderWidth={1} borderRadius={12} borderColor="gray.200" bg="white">
                <HStack justify="space-between" align="center" mb={3}>
                  <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                    Estado del análisis
                  </Text>
                  <Text fontSize="xs" color="gray.500">
                    {isAwaitingResponse ? "Esperando respuesta del modelo..." : `${Math.round(uploadProgress)}% completado`}
                  </Text>
                </HStack>
                {isAwaitingResponse ? (
                  <Progress
                    value={100}
                    colorScheme="teal"
                    size="lg"
                    hasStripe
                    isAnimated
                    isIndeterminate
                    borderRadius="full"
                  />
                ) : (
                  <Progress
                    value={uploadProgress}
                    colorScheme="teal"
                    size="lg"
                    hasStripe
                    isAnimated
                    borderRadius="full"
                  />
                )}
                <HStack justify="space-between" mt={3} spacing={2}>
                  <Box textAlign="center" flex={1}>
                    <Badge colorScheme={progressStage >= 0 ? "teal" : "gray"} variant={progressStage >= 0 ? "solid" : "subtle"}>
                      1
                    </Badge>
                    <Text fontSize="xs" color={progressStage >= 0 ? "teal.700" : "gray.500"} mt={1}>
                      Recibido
                    </Text>
                  </Box>
                  <Divider orientation="horizontal" />
                  <Box textAlign="center" flex={1}>
                    <Badge colorScheme={progressStage >= 1 ? "teal" : "gray"} variant={progressStage >= 1 ? "solid" : "subtle"}>
                      2
                    </Badge>
                    <Text fontSize="xs" color={progressStage >= 1 ? "teal.700" : "gray.500"} mt={1}>
                      Analizando
                    </Text>
                  </Box>
                  <Divider orientation="horizontal" />
                  <Box textAlign="center" flex={1}>
                    <Badge colorScheme={progressStage >= 2 ? "teal" : "gray"} variant={progressStage >= 2 ? "solid" : "subtle"}>
                      3
                    </Badge>
                    <Text fontSize="xs" color={progressStage >= 2 ? "teal.700" : "gray.500"} mt={1}>
                      Generando amenazas
                    </Text>
                  </Box>
                </HStack>
              </Box>

              <Box textAlign="center">
                {!isAwaitingResponse ? (
                  <Spinner
                    thickness="4px"
                    speed="0.65s"
                    emptyColor="gray.200"
                    color="teal.500"
                    size="xl"
                    animation={`${pulse} 2s infinite`}
                  />
                ) : (
                  <Box
                    mx="auto"
                    width="56px"
                    height="56px"
                    borderRadius="full"
                    border="2px solid"
                    borderColor="teal.200"
                    bg="teal.50"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Text fontSize="xs" fontWeight="bold" color="teal.700">OK</Text>
                  </Box>
                )}
                <Text mt={4} fontSize="lg" color="teal.600" fontWeight="medium">
                  {isAwaitingResponse ? "Esperando respuesta final del análisis..." : progressLabel}
                </Text>
              </Box>
              <Text fontSize="sm" color="gray.600" textAlign="center">
                🤖 El archivo ya fue enviado. Si ves este estado, el worker sigue analizando y la respuesta final aún no llega.
              </Text>
            </VStack>
          )}

          {/* ── SUCCESS STATE ── */}
          {uploadStatus === "success" && (
            <VStack spacing={4} animation={`${fadeIn} 0.5s ease-out`}>
              <Icon as={FiCheck} fontSize="4em" color="green.500" animation={`${bounce} 0.6s ease-out`} />
              <Text fontSize="xl" color="green.600" fontWeight="bold" textAlign="center">
                ¡Contenido procesado exitosamente! 🎉
              </Text>
              <Text fontSize="md" color="gray.600" textAlign="center">
                Redirigiendo al análisis de amenazas...
              </Text>
              <Progress size="xs" isIndeterminate colorScheme="green" width="200px" />
            </VStack>
          )}

          {/* ── SAVED (image uploaded, AI couldn't analyze) STATE ── */}
          {uploadStatus === "saved" && (
            <VStack spacing={4} animation={`${fadeIn} 0.5s ease-out`}>
              <Icon as={FiCheck} fontSize="4em" color="teal.500" animation={`${bounce} 0.6s ease-out`} />
              <Text fontSize="xl" color="teal.600" fontWeight="bold" textAlign="center">
                Archivo guardado ✓
              </Text>
              <Text fontSize="sm" color="gray.600" textAlign="center">
                El modelo AI no pudo detectar amenazas automáticamente en este archivo.
                Puedes agregarlas manualmente en el análisis.
              </Text>
              <Text fontSize="xs" color="gray.400" textAlign="center">
                Redirigiendo al análisis...
              </Text>
              <Progress size="xs" isIndeterminate colorScheme="teal" width="200px" />
            </VStack>
          )}

          {/* ── WARNING STATE ── */}
          {uploadStatus === "warning" && (
            <VStack spacing={4} animation={`${fadeIn} 0.3s ease-out`}>
              <Icon as={FiAlertCircle} fontSize="3em" color="orange.500" />
              <Text fontSize="lg" color="orange.600" fontWeight="medium">
                Aviso
              </Text>
              <Text fontSize="sm" color="gray.600" textAlign="center">
                {errorMessage || "No se encontraron amenazas en el contenido analizado"}
              </Text>
              <Button onClick={resetUpload} colorScheme="orange" variant="outline">
                Intentar de nuevo
              </Button>
            </VStack>
          )}

          {/* ── ERROR STATE ── */}
          {uploadStatus === "error" && (
            <VStack spacing={4} animation={`${fadeIn} 0.3s ease-out`}>
              <Icon as={FiAlertCircle} fontSize="3em" color="red.500" />
              <Text fontSize="lg" color="red.600" fontWeight="medium">
                {errorMessage?.startsWith('Sin créditos') ? '💳 Créditos insuficientes' : 'Error al procesar el contenido'}
              </Text>
              <Text fontSize="sm" color="gray.600" textAlign="center">
                {errorMessage || "Se ha producido un error durante el procesamiento. Por favor intente nuevamente."}
              </Text>
              <Button onClick={resetUpload} colorScheme="red" variant="outline">
                Intentar de nuevo
              </Button>
            </VStack>
          )}

          {/* ── SUBMIT / RESET BUTTONS ── */}
          {isReady && (
            <VStack spacing={3} width="100%">
              <Button
                onClick={handleSubmit}
                colorScheme="teal"
                size="lg"
                width="full"
                leftIcon={<FiUpload />}
                _hover={{ transform: "translateY(-2px)", boxShadow: "lg" }}
                transition="all 0.2s"
              >
                Analizar {mode === "file" ? "Archivo" : "Descripción"}
              </Button>
              <Button
                onClick={resetUpload}
                variant="ghost"
                size="sm"
                color="gray.500"
              >
                {mode === "file" ? "Seleccionar otro archivo" : "Limpiar texto"}
              </Button>
            </VStack>
          )}
        </VStack>
      </Box>
    </Flex>
  );
};

export default UploadDiagram;
